import { useEffect, useMemo, useState } from 'react';

import { AuditEntry, listAuditEntries } from '../api/audit';

const currentMonth = () => new Date().toISOString().slice(0, 7);

const shiftMonth = (month: string, delta: number): string => {
  const [year, mon] = month.split('-').map(Number);
  const date = new Date(Date.UTC(year, mon - 1 + delta, 1));
  return date.toISOString().slice(0, 7);
};

/** System actors ('system', email hashes) have no directory entry — show as-is, truncated. */
const formatActorId = (entry: AuditEntry) =>
  entry.actorId.length > 16 ? `${entry.actorId.slice(0, 16)}…` : entry.actorId;

export default function AdminAuditPage() {
  const [month, setMonth] = useState(currentMonth());
  const [items, setItems] = useState<AuditEntry[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    setError(null);
    setItems([]);
    setNextToken(undefined);
    listAuditEntries(month)
      .then((data) => {
        setItems(data.items);
        setNextToken(data.nextToken);
      })
      .catch(() => setError('Failed to load the audit log.'))
      .finally(() => setLoading(false));
  }, [month]);

  const loadMore = () => {
    if (!nextToken) {
      return;
    }
    setLoadingMore(true);
    listAuditEntries(month, nextToken)
      .then((data) => {
        setItems((prev) => [...prev, ...data.items]);
        setNextToken(data.nextToken);
      })
      .catch(() => setError('Failed to load more entries.'))
      .finally(() => setLoadingMore(false));
  };

  const actions = useMemo(() => [...new Set(items.map((i) => i.action))].sort(), [items]);
  const visible = actionFilter ? items.filter((i) => i.action === actionFilter) : items;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 16px' }}>
      <h2>Audit log</h2>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <button
          aria-label="Previous month"
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: '1px solid var(--border-strong)',
            background: 'var(--bg-surface)',
            color: 'var(--text-primary)',
            cursor: 'pointer',
            fontSize: 13,
          }}
          onClick={() => setMonth((m) => shiftMonth(m, -1))}
        >
          ‹
        </button>
        <span style={{ fontSize: 15, fontWeight: 600, minWidth: 70, textAlign: 'center' }}>
          {month}
        </span>
        <button
          aria-label="Next month"
          disabled={month >= currentMonth()}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: '1px solid var(--border-strong)',
            background: 'var(--bg-surface)',
            color: month >= currentMonth() ? 'var(--text-muted)' : 'var(--text-primary)',
            cursor: month >= currentMonth() ? 'default' : 'pointer',
            fontSize: 13,
          }}
          onClick={() => setMonth((m) => shiftMonth(m, 1))}
        >
          ›
        </button>

        <select
          disabled={actions.length === 0}
          style={{
            marginLeft: 'auto',
            padding: '6px 10px',
            borderRadius: 6,
            border: '1px solid var(--border-strong)',
            fontSize: 13,
          }}
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
        >
          <option value="">All actions</option>
          {actions.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </div>

      {loading && <p style={{ color: 'var(--text-muted)' }}>Loading…</p>}
      {error && <p style={{ color: 'var(--danger-text)' }}>{error}</p>}

      {!loading && !error && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border-subtle)', textAlign: 'left' }}>
              <th style={{ padding: '8px 4px' }}>Time</th>
              <th style={{ padding: '8px 4px' }}>Action</th>
              <th style={{ padding: '8px 4px' }}>Actor</th>
              <th style={{ padding: '8px 4px' }}>Target</th>
              <th style={{ padding: '8px 4px' }}>Details</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: '16px 4px', color: 'var(--text-muted)' }}>
                  {actionFilter
                    ? `No "${actionFilter}" entries in ${month}.`
                    : `No audit entries in ${month}.`}
                </td>
              </tr>
            )}
            {visible.map((entry) => (
              <tr
                key={`${entry.timestamp}-${entry.action}-${entry.targetId}`}
                style={{ borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'top' }}
              >
                <td style={{ padding: '8px 4px', whiteSpace: 'nowrap' }}>
                  {new Date(entry.timestamp).toLocaleString()}
                </td>
                <td style={{ padding: '8px 4px', fontWeight: 600 }}>{entry.action}</td>
                <td style={{ padding: '8px 4px' }}>
                  <span style={{ color: 'var(--text-muted)' }}>
                    {entry.actorType === 'cognito-user' ? 'user (legacy)' : entry.actorType}
                  </span>{' '}
                  <span style={{ fontFamily: 'monospace', fontSize: 11 }}>
                    {formatActorId(entry)}
                  </span>
                </td>
                <td style={{ padding: '8px 4px' }}>
                  <span style={{ color: 'var(--text-muted)' }}>{entry.targetType}</span>{' '}
                  {entry.targetId}
                </td>
                <td style={{ padding: '8px 4px', fontFamily: 'monospace', fontSize: 11 }}>
                  {entry.metadata && Object.keys(entry.metadata).length > 0
                    ? JSON.stringify(entry.metadata)
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {nextToken && !loading && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <button
            className="btn-primary btn-sm"
            disabled={loadingMore}
            onClick={() => void loadMore()}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
